import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getFirestore, collection, addDoc, serverTimestamp, doc, deleteDoc } from "firebase/firestore";
import app from "./firebase-config";

const messaging = getMessaging(app);
const functions = getFunctions(app);
const db = getFirestore(app);

const getPlatform = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) {
    return "android";
  }
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return "ios";
  }
  return "web";
};

/**
 * Requests permission to show notifications, retrieves the FCM token,
 * and calls a cloud function to save it. This function is cross-platform
 * and handles web, Android (via Appilix), and iOS (via WebKit).
 * @param {string} userId The ID of the current user.
 */
export const requestNotificationPermission = async (userId) => {
  if (!userId) {
    console.error("User ID is required to save notification token.");
    return;
  }

  const platform = getPlatform();
  const saveToken = httpsCallable(functions, 'saveFcmToken');

  // --- Mobile App (WebView) Logic ---
  if (window.appilix || (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.appilix)) {
    console.log(`Appilix environment detected on ${platform}. Requesting FCM token...`);
    
    const appilixInterface = window.appilix || window.webkit.messageHandlers.appilix;

    // Listen for the token response
    const messageListener = async (event) => {
      try {
        // For iOS, the event itself might be the data. For Android, it's in event.data
        const eventData = event.data ? event.data : event;
        const data = typeof eventData === 'string' ? JSON.parse(eventData) : eventData;
        
        if (data.token) {
          const fcmToken = data.token;
          console.log("Firebase Token Received from WebView:", fcmToken);
          
          await saveToken({ token: fcmToken, platform: platform });
          console.log(`FCM token for ${platform} device sent to cloud function.`);
        }
      } catch (error) {
        console.error("Error processing message from Appilix WebView:", error);
      } finally {
        // Clean up listener to avoid multiple executions
        if (window.appilix) {
          window.appilix.onmessage = null;
        }
        // Note: iOS might require a different way to remove the listener if it's added differently.
      }
    };

    if (window.appilix) {
      window.appilix.onmessage = messageListener;
    } else {
      // For iOS, you might need a global function that the native code calls
      // This setup assumes the native side can handle this listener directly.
      // Or you might need a more specific setup if the iOS handler works differently.
      console.warn("iOS listener setup is assumed. Verify native implementation.");
    }

    // Post message to request the token
    appilixInterface.postMessage(JSON.stringify({
      type: "firebase_token"
    }));

    return; // Exit after handling webview logic
  }

  // --- Standard Web Browser Logic ---
  console.log("Standard web browser detected. Requesting FCM token via VAPID key.");
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.log("Unable to get permission to notify.");
      return;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.error("VAPID key is missing. Cannot get token.");
      return;
    }

    const currentToken = await getToken(messaging, { vapidKey: vapidKey });

    if (currentToken) {
      console.log("FCM Token:", currentToken);
      await saveToken({ token: currentToken, platform: platform });
      console.log(`FCM token for ${platform} device sent to cloud function.`);
    } else {
      console.log("No registration token available. Request one with getToken().");
    }
  } catch (error)
  {
    console.error("An error occurred while retrieving token or calling function. ", error);
  }
};

/**
 * Requests App Tracking Transparency permission on iOS via the Appilix webview bridge.
 * This function is designed to be called once after the user has entered the main app.
 */
export const requestATTPermission = () => {
  const platform = getPlatform();

  if (platform === "ios" && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.appilix) {
    console.log("iOS Appilix environment detected. Requesting App Tracking Transparency permission...");
    window.webkit.messageHandlers.appilix.postMessage(JSON.stringify({
      type: "request_att_permission"
    }));
  } else if (window.appilix) {
    console.log("Android Appilix environment detected. Requesting App Tracking Transparency permission...");
    window.appilix.postMessage(JSON.stringify({
      type: "request_att_permission"
    }));
  } else {
    console.log("Not in an Appilix webview or not on iOS. Skipping ATT request.");
  }
};

/**
 * Deletes the user's FCM token from Firestore.
 * @param {string} userId The ID of the current user.
 */
export const deleteNotificationToken = async (userId) => {
  if (!userId) {
    console.error("User ID is required to delete notification token.");
    return;
  }

  try {
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.error("VAPID key is missing. Cannot get token to delete.");
      return;
    }

    const currentToken = await getToken(messaging, { vapidKey: vapidKey });
    if (currentToken) {
      const tokenRef = doc(db, `artifacts/whispers-of-harmony/users/${userId}/fcm_tokens/${currentToken}`);
      await deleteDoc(tokenRef);
      console.log("FCM token deleted from Firestore.");
    }
  } catch (error) {
    console.error("An error occurred while deleting token. ", error);
  }
};

/**
 * Sets up a listener for foreground push notifications and triggers the in-app toast system.
 * @param {string} userId The ID of the current user.
 */
export const onForegroundMessage = (userId) => {
  if (!userId) return;

  onMessage(messaging, (payload) => {
    console.log("Foreground message received: ", payload);

    // Safely extract title and body from either notification or data payload
    const title = payload.notification?.title || payload.data?.title || 'New Notification';
    const body = payload.notification?.body || payload.data?.body || 'You have a new message.';

    // To trigger the existing toast system, write to the toast_notifications collection.
    const toastRef = collection(db, "artifacts", "whispers-of-harmony", "users", userId, "toast_notifications");
    addDoc(toastRef, {
      fromUserName: title, // <-- FIX: Use the correct field name
      message: body,      // <-- FIX: Use the correct field name
      data: payload.data || {},
      timestamp: serverTimestamp(),
    });
  });
};